import { useEffect, useState } from "react";
import api, { normalizeDecimal, DECIMAL_HINT, containerToBase, containerBase, containerFactor, convertQty } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { TypeBadge, MhdBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { Plus, Minus, History, ArrowUpDown, Package, Save, Search, ScanLine, MapPin, AlertTriangle, MoreHorizontal, SlidersHorizontal, Boxes, CalendarClock, PackageX } from "lucide-react";
import LabelPickerButton from "../components/LabelPickerButton";
import { toast } from "sonner";

export default function Stock() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kpis, setKpis] = useState({ total: 0, critical: 0, under: 0, mhd: 0 });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyItem, setHistoryItem] = useState(null);
  const [moveItem, setMoveItem] = useState(null);
  // form: qty_containers = Anzahl Gebinde; qty_direct = manuelle Einzelmenge; unit_direct = kg/g/l/ml/stk
  const [form, setForm] = useState({ item_id: "", type: "in", qty_containers: "", qty_direct: "", unit_direct: "", notes: "" });
  const [batches, setBatches] = useState([]);
  const [batchSel, setBatchSel] = useState({});   // Runde 117: Multi-Select fürs Bulk-Etikettendrucken
  const [batchId, setBatchId] = useState("");
  const [bundleSize, setBundleSize] = useState("1");   // Runde 124.8: Fertigware-Bundles
  const [shelfCode, setShelfCode] = useState("");      // Runde 124.9: Zielregal für Position 0
  const [extraPositions, setExtraPositions] = useState([]); // Runde 124.9: Multi-Position-Buchung
  const [avgAbsMovement, setAvgAbsMovement] = useState(0); // Runde 124.8: Sanity-Check
  const [batchEditOpen, setBatchEditOpen] = useState(false);
  const [batchEditItem, setBatchEditItem] = useState(null);
  const [batchEditRows, setBatchEditRows] = useState([]);
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [newItem, setNewItem] = useState({ sku: "", name: "", type: "raw", unit: "kg", barcode: "" });
  const [scanMode, setScanMode] = useState(() => localStorage.getItem("popcrn_scan_mode") || "in");
  const [scanHistory, setScanHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("popcrn_scan_history") || "[]"); }
    catch { return []; }
  });

  const pushScan = (entry) => {
    setScanHistory((prev) => {
      const next = [entry, ...prev].slice(0, 20);
      try { localStorage.setItem("popcrn_scan_history", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const PAGE_SIZE = 60;
  const buildParams = (pageArg) => {
    const byGroup = { type: [], status: [], loc: [] };
    filters.forEach((k) => {
      const f = FILTER_CHIPS.find((x) => x.key === k);
      if (f?.group) byGroup[f.group].push(k);
    });
    const params = { sort, page: pageArg, page_size: PAGE_SIZE };
    if (q.trim()) params.q = q.trim();
    if (byGroup.type.length) params.type = byGroup.type.join(",");
    if (byGroup.status.length) params.status = byGroup.status.join(",");
    if (byGroup.loc.length) params.loc = byGroup.loc.join(",");
    return params;
  };
  const load = async (pageArg = 1, append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock", { params: buildParams(pageArg) });
      const items = data.items || [];
      setRows((prev) => (append ? [...prev, ...items] : items));
      setTotal(data.total || 0);
      setKpis(data.kpis || { total: 0, critical: 0, under: 0, mhd: 0 });
      setPage(data.page || pageArg);
    } catch { if (!append) setRows([]); }
    finally { setLoading(false); }
  };
  // Scanner lookup: check loaded rows first, fall back to a server search so
  // items outside the current page/filter can still be found by barcode/SKU.
  const findByCode = async (raw) => {
    const match = (it) => String(it.barcode || "").trim() === raw
      || String(it.sku || "").trim().toLowerCase() === raw.toLowerCase();
    const local = rows.find(match);
    if (local) return local;
    try {
      const { data } = await api.get("/stock", { params: { q: raw, page_size: 10 } });
      return (data.items || []).find(match) || null;
    } catch { return null; }
  };

  // --- Card-UI: Filter (kombinierbar), Sortierung, Scanner-Panel, Detail-Drawer ---
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState("critical");
  const [scanPanelOpen, setScanPanelOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailBatches, setDetailBatches] = useState([]);

  const FILTER_CHIPS = [
    { key: "all", label: "Alle" },
    { key: "critical", label: "Kritisch", group: "status" },
    { key: "under", label: "Unter Minimum", group: "status" },
    { key: "mhd", label: "MHD", group: "status" },
    { key: "raw", label: "Rohstoffe", group: "type" },
    { key: "packaging", label: "Verpackungen", group: "type" },
    { key: "finished", label: "Fertigwaren", group: "type" },
    { key: "with_loc", label: "Mit Lagerplatz", group: "loc" },
    { key: "without_loc", label: "Ohne Lagerplatz", group: "loc" },
  ];
  const toggleFilter = (key) => {
    if (key === "all") return setFilters([]);
    setFilters((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const num = (v, d = 3) => Number(v ?? 0).toLocaleString("de-DE", { maximumFractionDigits: d });
  const hasLoc = (r) => !!(r.warehouse_location && String(r.warehouse_location).trim());
  const statusOf = (r) => {
    const av = Number(r.available ?? r.stock ?? 0);
    const min = Number(r.min_stock || 0);
    if (av < 0) return "critical";
    if (av < min) return "warning";
    return "ok";
  };
  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return null; }
  };
  const stockPct = (r) => {
    const s = Number(r.stock || 0);
    const target = Number(r.max_stock) > 0 ? Number(r.max_stock)
                 : Number(r.min_stock) > 0 ? Number(r.min_stock) * 1.5
                 : (s > 0 ? s : 1);
    return Math.max(0, Math.min(100, target > 0 ? (s / target) * 100 : 0));
  };
  const openDetail = async (item) => {
    setDetailItem(item);
    setDetailBatches([]);
    try {
      const { data } = await api.get("/batches", { params: { item_id: item.id } });
      setDetailBatches(data || []);
    } catch { setDetailBatches([]); }
  };

  // Server-driven list: refetch (debounced) whenever search, filters or sort
  // change. Pagination is additive via the "Mehr laden" button.
  useEffect(() => {
    const t = setTimeout(() => load(1, false), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filters, sort]);

  useEffect(() => {
    if (!form.item_id) { setBatches([]); return; }
    api.get("/batches", { params: { item_id: form.item_id } })
       .then((r) => setBatches(r.data))
       .catch(() => setBatches([]));
    setBatchId("");
  }, [form.item_id]);

  const openMovement = (item, type = "in") => {
    setMoveItem(item);
    setForm({
      item_id: item.id, type,
      qty_containers: item.container_size ? "1" : "",
      qty_direct: "",
      unit_direct: item.unit || "stk",
      notes: "",
    });
    setBatchId("");
    setBundleSize("1");
    setShelfCode("");
    setExtraPositions([]);
    setMoveOpen(true);
    // Pre-compute average |quantity| of the last 30 movements for sanity checks.
    api.get("/stock/movements", { params: { item_id: item.id, limit: 30 } })
       .then((r) => {
         const arr = (r.data || []).map((m) => Math.abs(Number(m.quantity) || 0)).filter((n) => n > 0);
         setAvgAbsMovement(arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
       })
       .catch(() => setAvgAbsMovement(0));
  };

  /**
   * Compute the booking amount in the item's OWN unit
   * (whatever the user configured on the item — kg, g, l, ml, stk).
   *
   * Runde 125.6: Der Bestand wird konsequent in der Artikel-Einheit gehalten.
   * Wir konvertieren die Nutzereingabe (mit ihrer eigenen Einheit) über
   * `convertQty()` in die Artikel-Einheit, statt sie stur in Gramm zu
   * multiplizieren. Alte Logik (`d * 1000` für kg) hat kg-Artikel um Faktor
   * 1000 überbucht.
   *
   * Regeln:
   *   - Direct: `convertQty(qty_direct, unit_direct, item.unit)`
   *   - Gebinde: `qty_containers × container_size` → per convertQty aus dem
   *     `container_unit` in die `item.unit` überführt (z. B. 25 kg-Sack für
   *     einen g-Artikel → 25 000 g gespeichert).
   *   - qty_containers wird ignoriert sobald qty_direct gesetzt ist.
   */
  const computedQty = () => {
    if (!moveItem) return 0;
    const c = Number(String(form.qty_containers || "").replace(",", "."));
    const d = Number(String(form.qty_direct || "").replace(",", "."));
    const hasDirect = form.qty_direct !== "" && form.qty_direct != null
                      && isFinite(d) && d !== 0;
    const bs = Math.max(1, Number(bundleSize) || 1);
    const isBundleable = ["finished", "bundle"].includes((moveItem.type || "").toLowerCase());
    if (hasDirect) {
      const u = (form.unit_direct || moveItem.unit || "").toLowerCase();
      const converted = convertQty(d, u, moveItem.unit);
      return isBundleable && u === "stk" ? converted * bs : converted;
    }
    if (isFinite(c) && c > 0 && moveItem.container_size) {
      const cu = moveItem.container_unit || moveItem.unit || "kg";
      return convertQty(c * Number(moveItem.container_size), cu, moveItem.unit);
    }
    return 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    // Assemble all positions to book: primary form + extra rows.
    const positions = [];
    const mainQty = computedQty();
    if (mainQty !== 0) {
      positions.push({
        quantity: mainQty,
        batch_id: batchId || null,
        shelf_code: shelfCode || null,
        notes: form.notes || null,
      });
    }
    for (const p of extraPositions) {
      const bs = Math.max(1, Number(p.bundle_size) || 1);
      const raw = Number(String(p.quantity || "").replace(",", "."));
      if (!isFinite(raw) || raw === 0) continue;
      positions.push({
        quantity: raw * bs,
        batch_id: p.batch_id || null,
        shelf_code: p.shelf_code || null,
        notes: p.notes || null,
      });
    }
    if (positions.length === 0) return toast.error("Menge muss > 0 sein");
    // Sanity: warn once if the total absolute booked amount is > 10× avg.
    const total = positions.reduce((s, p) => s + Math.abs(p.quantity), 0);
    if (avgAbsMovement > 0 && total > 10 * avgAbsMovement && !form._sanity_confirmed) {
      setForm((f) => ({ ...f, _sanity_confirmed: true }));
      return toast.warning(
        `Achtung: ${total.toLocaleString("de-DE", { maximumFractionDigits: 3 })} ${moveItem.unit || ""} über alle Positionen sind > 10× Durchschnitt (${avgAbsMovement.toFixed(2)} ${moveItem.unit || ""}). Nochmal auf Speichern klicken zum Bestätigen.`
      );
    }
    try {
      // Post one stock movement per position, tagging the shelf in `notes`.
      // (Shelf-scoped stock is derived from movements via reference lookup.)
      for (const p of positions) {
        const refNote = [
          p.notes,
          p.shelf_code ? `Regal ${p.shelf_code}` : null,
        ].filter(Boolean).join(" · ") || null;
        await api.post("/stock/movements", {
          item_id: form.item_id,
          type: form.type,
          quantity: form.type === "adjustment" ? p.quantity : Math.abs(p.quantity),
          batch_id: p.batch_id,
          notes: refNote,
        });
      }
      toast.success(`${positions.length} Position${positions.length === 1 ? "" : "en"} gebucht`);
      setMoveOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const openHistory = async (item) => {
    setHistoryItem(item);
    try {
      const { data } = await api.get("/stock/movements", { params: { item_id: item.id, limit: 200 } });
      setHistoryRows(data);
    } catch (_e) { setHistoryRows([]); }
    setHistoryOpen(true);
  };

  const openBatchEdit = async (item) => {
    setBatchEditItem(item);
    try {
      const { data } = await api.get("/batches", { params: { item_id: item.id } });
      setBatchEditRows((data || []).map((b) => ({ ...b, _dirty: false })));
    } catch (_e) { setBatchEditRows([]); }
    setBatchEditOpen(true);
  };

  const updateBatchField = (id, field, value) => {
    setBatchEditRows((prev) => prev.map((b) =>
      b.id === id ? { ...b, [field]: value, _dirty: true } : b));
  };

  const saveBatch = async (batch) => {
    try {
      await api.patch(`/batches/${batch.id}`, {
        charge_number: batch.charge_number,
        mhd: batch.mhd,
      });
      toast.success(`Charge ${batch.charge_number} gespeichert`);
      setBatchEditRows((prev) => prev.map((b) =>
        b.id === batch.id ? { ...b, _dirty: false } : b));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler beim Speichern"); }
  };

  const moveTypeLabel = (t) => ({
    in: "Zugang", out: "Abgang", adjustment: "Korrektur",
    production_in: "Prod. Zugang", production_out: "Prod. Verbrauch",
  }[t] || t);

  return (
    <div data-testid="page-stock">
      <PageHeader
        eyebrow="Warehouse"
        title="Lagerbestand"
        description="Live-Bestand aller Artikel. Manuelle Zu-/Abgänge und Bestandskorrektur direkt aus der Liste."
      />

      {/* Toolbar: Suche · Scanner · Sortierung */}
      <div className="mb-4 flex flex-col gap-3" data-testid="stock-toolbar">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="input-stock-search"
                   placeholder="Artikel, SKU oder Regal suchen…"
                   className="pl-9 h-11 rounded-full" />
          </div>
          <button type="button" onClick={() => setScanPanelOpen((s) => !s)} data-testid="btn-toggle-scanner"
                  className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border text-sm font-semibold transition-colors ${scanPanelOpen ? "bg-emerald-600 text-white border-emerald-600" : "border-[var(--border)] text-[var(--text)] hover:bg-black/5"}`}>
            <ScanLine size={16} /> Scanner
          </button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="select-stock-sort" className="h-11 rounded-full w-full sm:w-[200px]">
              <span className="inline-flex items-center gap-2"><SlidersHorizontal size={14} /><SelectValue /></span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="critical">Kritische zuerst</SelectItem>
              <SelectItem value="name-asc">Name A–Z</SelectItem>
              <SelectItem value="name-desc">Name Z–A</SelectItem>
              <SelectItem value="stock-asc">Bestand ↑</SelectItem>
              <SelectItem value="stock-desc">Bestand ↓</SelectItem>
              <SelectItem value="avail-asc">Verfügbar ↑</SelectItem>
              <SelectItem value="mhd-asc">MHD früh → spät</SelectItem>
              <SelectItem value="shelf">Regal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Filter-Chips (kombinierbar) */}
        <div className="flex flex-wrap gap-2" data-testid="stock-filters">
          {FILTER_CHIPS.map((f) => {
            const active = f.key === "all" ? filters.length === 0 : filters.includes(f.key);
            return (
              <button key={f.key} type="button" onClick={() => toggleFilter(f.key)} data-testid={`filter-${f.key}`}
                      className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[var(--pop-pink)] text-white border-[var(--pop-pink)]" : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)]"}`}>
                {f.label}
              </button>
            );
          })}
        </div>

        {/* KPI-Kacheln (dynamisch) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stock-kpis">
          {[
            { key: "artikel", label: "Artikel", val: kpis.total, cls: "text-[var(--text)]", ring: "border-[var(--border)]" },
            { key: "kritisch", label: "Kritisch", val: kpis.critical, cls: "text-red-600", ring: "border-red-500/40" },
            { key: "unter-min", label: "Unter Minimum", val: kpis.under, cls: "text-amber-600", ring: "border-amber-400/50" },
            { key: "mhd", label: "MHD-Warnung", val: kpis.mhd, cls: "text-orange-500", ring: "border-orange-400/50" },
          ].map((k) => (
            <div key={k.key} data-testid={`kpi-${k.key}`} className={`rounded-2xl border ${k.ring} bg-[var(--surface)] p-4`}>
              <div className="overline text-[10px] text-[var(--text-muted)]">{k.label}</div>
              <div className={`font-display font-black text-3xl mt-1 ${k.cls}`}>{k.val}</div>
            </div>
          ))}
        </div>

        {/* Scanner-Panel (einklappbar) — vollständige Scan-Funktion bleibt erhalten */}
        {scanPanelOpen && (
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-3 items-start rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-3" data-testid="scan-wrapper">
            <div className="space-y-1.5">
              <div className="inline-flex rounded-full border border-emerald-300 bg-white p-0.5 text-[11px] font-semibold" data-testid="scan-mode-toggle">
                {[
                  { v: "in", label: "Zugang", cls: "bg-emerald-600 text-white", icon: Plus },
                  { v: "out", label: "Abgang", cls: "bg-red-600 text-white", icon: Minus },
                  { v: "adjustment", label: "Korrektur", cls: "bg-amber-500 text-white", icon: ArrowUpDown },
                ].map(({ v, label, cls, icon: Ic }) => (
                  <button key={v} type="button"
                          data-testid={`btn-scan-mode-${v}`}
                          onClick={() => { setScanMode(v); try { localStorage.setItem("popcrn_scan_mode", v); } catch {} }}
                          className={`px-3 h-7 rounded-full inline-flex items-center gap-1 ${scanMode === v ? cls : "text-neutral-600 hover:bg-neutral-100"}`}>
                    <Ic size={11} /> {label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <ScanLine size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600" />
                <Input
                  data-testid="input-global-scan"
                  placeholder={`Scannen → ${scanMode === "in" ? "Zugang" : scanMode === "out" ? "Abgang" : "Korrektur"} öffnen…`}
                  className="pl-9 h-10 font-mono border-emerald-300 focus-visible:ring-emerald-400"
                  onKeyDown={async (e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    const raw = (e.currentTarget.value || "").trim();
                    if (!raw) return;
                    e.currentTarget.value = "";
                    const now = new Date().toISOString();
                    const hit = await findByCode(raw);
                    if (!hit) {
                      pushScan({ ts: now, code: raw, sku: null, name: null, matched: false });
                      toast.error(`Kein Artikel für „${raw}" gefunden`);
                      return;
                    }
                    pushScan({ ts: now, code: raw, sku: hit.sku, name: hit.name, matched: true, mode: scanMode });
                    openMovement(hit, scanMode);
                    const label = scanMode === "in" ? "Zugang" : scanMode === "out" ? "Abgang" : "Korrektur";
                    toast.success(`${hit.sku} · ${label} geöffnet`);
                  }}
                />
              </div>
            </div>
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-2 max-h-[220px] overflow-y-auto" data-testid="scan-history">
              <div className="flex items-center justify-between mb-1.5 px-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Scan-Historie · letzte 20</div>
                {scanHistory.length > 0 && (
                  <button type="button" onClick={() => { setScanHistory([]); try { localStorage.removeItem("popcrn_scan_history"); } catch {} }}
                          data-testid="btn-clear-scan-history"
                          className="text-[10px] text-[var(--text-muted)] hover:text-red-600">Leeren</button>
                )}
              </div>
              {scanHistory.length === 0 ? (
                <div className="text-[11px] italic text-[var(--text-muted)] p-2">Noch keine Scans.</div>
              ) : (
                <div className="divide-y">
                  {scanHistory.map((s, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-2 py-1.5 text-[11px]"
                         data-testid={`scan-entry-${i}`}>
                      <div className="min-w-0">
                        {s.matched ? (
                          <>
                            <span className="font-mono text-[10px] text-[var(--text-muted)]">{s.sku}</span>
                            {" "}
                            <span className="truncate">{s.name}</span>
                          </>
                        ) : (
                          <button type="button"
                                  data-testid={`btn-create-from-scan-${i}`}
                                  onClick={() => {
                                    setNewItem({ sku: "", name: "", type: "raw", unit: "kg", barcode: s.code });
                                    setNewItemOpen(true);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:underline text-left font-semibold"
                                  title="Neuen Artikel mit diesem Barcode anlegen">
                            ? {s.code} · anlegen
                          </button>
                        )}
                        <div className="font-mono text-[9px] text-[var(--text-muted)]">{s.code}</div>
                      </div>
                      <div className="font-mono text-[9px] text-[var(--text-muted)] shrink-0">
                        {new Date(s.ts).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Artikel-Karten */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="stock-grid">
        {rows.map((r) => {
          const st = statusOf(r);
          const av = Number(r.available ?? r.stock ?? 0);
          const barColor = st === "critical" ? "bg-red-500" : st === "warning" ? "bg-amber-500" : "bg-emerald-500";
          const ringColor = st === "critical" ? "border-red-500/50" : st === "warning" ? "border-amber-400/50" : "border-[var(--border)]";
          const availColor = st === "critical" ? "text-red-600" : st === "warning" ? "text-amber-600" : "text-[var(--text)]";
          return (
            <div key={r.id} data-testid={`stock-card-${r.sku}`} onClick={() => openDetail(r)}
                 role="button" tabIndex={0}
                 onKeyDown={(e) => { if (e.key === "Enter") openDetail(r); }}
                 className={`group relative overflow-hidden rounded-2xl border ${ringColor} bg-[var(--surface)] p-4 cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pop-pink)]`}>
              <span className={`absolute left-0 top-0 h-full w-1 ${barColor}`} />
              {/* Kopf */}
              <div className="flex items-start justify-between gap-2 pl-2">
                <div className="min-w-0">
                  <div className="font-mono text-[11px] text-[var(--text-muted)] truncate">{r.sku}</div>
                  <div className="font-display font-bold text-base leading-tight truncate text-[var(--text)]" title={r.name}>{r.name}</div>
                  <div className="mt-1"><TypeBadge type={r.type} /></div>
                </div>
                {st !== "ok" && (
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${st === "critical" ? "bg-red-500/15 text-red-600" : "bg-amber-500/15 text-amber-700"}`}>
                    <AlertTriangle size={11} /> {st === "critical" ? "KRITISCH" : "WARNUNG"}
                  </span>
                )}
              </div>
              {/* Kennzahlen */}
              <div className="mt-3 pl-2 grid grid-cols-3 gap-2">
                <div className="min-w-0">
                  <div className="overline text-[10px] text-[var(--text-muted)]">Verfügbar</div>
                  <div className={`font-mono font-black text-[15px] leading-none truncate ${availColor}`} title={`${num(av)} ${r.unit}`} data-testid={`card-available-${r.sku}`}>{num(av)}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{r.unit}</div>
                </div>
                <div className="min-w-0">
                  <div className="overline text-[10px] text-[var(--text-muted)]">Bestand</div>
                  <div className="font-mono font-bold text-[15px] leading-none truncate text-[var(--text)]" title={`${num(r.stock)} ${r.unit}`}>{num(r.stock)}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{r.unit}</div>
                </div>
                <div className="min-w-0">
                  <div className="overline text-[10px] text-[var(--text-muted)]">Reserviert</div>
                  <div className="font-mono font-semibold text-[15px] leading-none truncate text-[var(--text-muted)]" title={Number(r.reserved || 0) > 0 ? `${num(r.reserved)} ${r.unit}` : "—"}>{Number(r.reserved || 0) > 0 ? num(r.reserved) : "—"}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{Number(r.reserved || 0) > 0 ? r.unit : ""}</div>
                </div>
              </div>
              {/* Bestandsbalken */}
              <div className="mt-3 pl-2">
                <div className="h-1.5 w-full rounded-full bg-[var(--border)] overflow-hidden">
                  <div className={`h-full ${barColor}`} style={{ width: `${stockPct(r)}%` }} />
                </div>
                <div className="mt-1 flex justify-between text-[10px] text-[var(--text-muted)]">
                  <span>Min {num(r.min_stock, 0)}</span>
                  {Number(r.max_stock) > 0 && <span>Max {num(r.max_stock, 0)}</span>}
                </div>
              </div>
              {/* Lagerort + MHD */}
              <div className="mt-3 pl-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="overline text-[10px] text-[var(--text-muted)] flex items-center gap-1"><MapPin size={11} /> Lagerort</div>
                  <div className="text-sm font-semibold text-[var(--text)] truncate">
                    {hasLoc(r) ? r.warehouse_location : <span className="text-[var(--text-muted)] font-normal">Kein Lagerplatz</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="overline text-[10px] text-[var(--text-muted)] flex items-center gap-1 justify-end"><CalendarClock size={11} /> MHD</div>
                  {r.nearest_mhd ? (
                    <>
                      <div className="text-sm font-semibold text-[var(--text)]">{fmtDate(r.nearest_mhd)}</div>
                      <div className="text-[10px] mt-0.5"><MhdBadge days={r.days_to_mhd} /></div>
                    </>
                  ) : <div className="text-sm text-[var(--text-muted)]">—</div>}
                </div>
              </div>
              {/* Aktionen */}
              <div className="mt-3 pl-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <button data-testid={`btn-stock-in-${r.sku}`} onClick={() => openMovement(r, "in")}
                        className="flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20">
                  <Plus size={13} /> Zugang
                </button>
                <button data-testid={`btn-stock-out-${r.sku}`} onClick={() => openMovement(r, "out")}
                        className="flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-lg text-xs font-semibold bg-red-500/10 text-red-600 hover:bg-red-500/20">
                  <Minus size={13} /> Abgang
                </button>
                <button data-testid={`btn-stock-adj-${r.sku}`} onClick={() => openMovement(r, "adjustment")}
                        className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold bg-amber-500/10 text-amber-700 hover:bg-amber-500/20">
                  <ArrowUpDown size={13} /> Korr.
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`btn-stock-more-${r.sku}`}
                            className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[var(--text-muted)] hover:bg-[var(--border)]">
                      <MoreHorizontal size={16} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem data-testid={`btn-stock-batches-${r.sku}`} onClick={() => openBatchEdit(r)}>
                      <Package size={14} className="mr-2" /> Chargen
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`btn-stock-hist-${r.sku}`} onClick={() => openHistory(r)}>
                      <History size={14} className="mr-2" /> Verlauf
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openDetail(r)}>
                      <Boxes size={14} className="mr-2" /> Details
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>
      {rows.length > 0 && rows.length < total && (
        <div className="mt-6 flex justify-center" data-testid="stock-loadmore-wrap">
          <button data-testid="btn-stock-loadmore" onClick={() => load(page + 1, true)} disabled={loading}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-[var(--border)] bg-[var(--surface)] text-sm font-semibold hover:bg-black/5 disabled:opacity-50">
            {loading ? "Lädt…" : `Mehr laden (${rows.length}/${total})`}
          </button>
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="text-center py-20" data-testid="stock-empty">
          <PackageX size={40} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <div className="font-display font-bold text-lg text-[var(--text)]">Keine Artikel gefunden</div>
          <div className="text-sm text-[var(--text-muted)] mt-1">Passe deine Suche oder Filter an.</div>
          <button data-testid="btn-reset-filters" onClick={() => { setQ(""); setFilters([]); }}
                  className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--pop-pink)] text-white text-sm font-semibold">
            Filter zurücksetzen
          </button>
        </div>
      )}

      {/* Movement dialog */}
      <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-inline-new-item">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Neuen Artikel anlegen</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!newItem.sku || !newItem.name) return toast.error("SKU und Name sind Pflicht");
            try {
              const { data } = await api.post("/items", {
                sku: newItem.sku, name: newItem.name, type: newItem.type,
                unit: newItem.unit, barcode: newItem.barcode || null,
                min_stock: 0, vat_rate: 0.07,
              });
              toast.success(`Artikel „${data.sku}" angelegt`);
              setNewItemOpen(false);
              await load();
              const created = { ...data, stock: 0 };
              openMovement(created, "in");
            } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
          }} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs">
              Barcode <span className="font-mono">{newItem.barcode || "—"}</span> wird gespeichert. Nach dem Anlegen öffnet sich direkt der Zugang-Dialog.
            </div>
            <div className="col-span-1"><Label>SKU *</Label>
              <Input required data-testid="input-newitem-sku" value={newItem.sku}
                     onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })} />
              {newItem.sku.trim().length >= 2 && (() => {
                const s = newItem.sku.trim().toLowerCase();
                const barcode = (newItem.barcode || "").trim();
                // Levenshtein-Distanz (klassisch) — max 20 Zeichen betrachtet
                const lev = (a, b) => {
                  a = (a || "").slice(0, 20); b = (b || "").slice(0, 20);
                  if (!a.length) return b.length; if (!b.length) return a.length;
                  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i]);
                  for (let j = 1; j <= b.length; j++) dp[0][j] = j;
                  for (let i = 1; i <= a.length; i++)
                    for (let j = 1; j <= b.length; j++)
                      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1,
                                            dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
                  return dp[a.length][b.length];
                };
                const scored = rows
                  .filter((it) => (it.sku || "").toLowerCase() !== s)
                  .map((it) => {
                    const isu = (it.sku || "").toLowerCase();
                    const d = lev(isu, s);
                    // Score: kleiner = besser. Substring wird bevorzugt (−3),
                    // Barcode-Präfix-Match kickt ans Ende auf −10 (Top-Position).
                    let score = d;
                    if (isu.includes(s)) score -= 3;
                    if (barcode.length >= 3 && (it.barcode || "").startsWith(barcode.slice(0, Math.min(6, barcode.length)))) {
                      score -= 10;
                    }
                    return { it, score, d };
                  })
                  .filter((x) => x.score <= 3 || x.d <= 3)
                  .sort((a, b) => a.score - b.score)
                  .slice(0, 5);
                if (scored.length === 0) return null;
                return (
                  <div className="mt-1.5 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 leading-relaxed" data-testid="sku-suggestions">
                    <div className="font-semibold mb-0.5">Meintest du?</div>
                    <div className="flex flex-wrap gap-1">
                      {scored.map(({ it, score }) => (
                        <button type="button" key={it.id}
                                data-testid={`sku-suggestion-${it.sku}`}
                                onClick={() => setNewItem({ ...newItem, sku: it.sku, name: newItem.name || it.name || "" })}
                                title={`${it.name}${it.barcode ? " · EAN " + it.barcode : ""}`}
                                className={`font-mono text-[10px] bg-white border rounded px-1.5 py-0.5 ${score < 0 ? "border-emerald-400 text-emerald-800" : "border-amber-300 hover:border-amber-500"}`}>
                          {it.sku}{score <= -10 ? " · Barcode-Match" : ""}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="col-span-1"><Label>Barcode / EAN</Label>
              <Input value={newItem.barcode} className="font-mono"
                     onChange={(e) => setNewItem({ ...newItem, barcode: e.target.value })} /></div>
            <div className="col-span-2"><Label>Name *</Label>
              <Input required data-testid="input-newitem-name" value={newItem.name}
                     onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} /></div>
            <div><Label>Typ</Label>
              <Select value={newItem.type} onValueChange={(v) => setNewItem({ ...newItem, type: v })}>
                <SelectTrigger data-testid="select-newitem-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="raw">Rohstoff</SelectItem>
                  <SelectItem value="packaging">Verpackung</SelectItem>
                  <SelectItem value="finished">Fertigware</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Einheit</Label>
              <Input value={newItem.unit}
                     onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} placeholder="kg / stk / g" />
            </div>
            <DialogFooter className="col-span-2">
              <Button type="submit" data-testid="btn-save-newitem"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                Anlegen &amp; Zugang öffnen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Bestandsbuchung</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-2 text-xs text-neutral-700">
              <span className="text-[var(--text-muted)]">Artikel:</span> <b>{moveItem?.name}</b>
              {moveItem?.container_size && (
                <> · <span className="text-[var(--text-muted)]">Gebinde:</span> <b>{moveItem.container_label || "Gebinde"} à {moveItem.container_size} {moveItem.container_unit || "kg"}</b></>
              )}
              {moveItem?.barcode && (
                <> · <span className="text-[var(--text-muted)]">EAN:</span> <span className="font-mono">{moveItem.barcode}</span></>
              )}
            </div>

            {form.type === "adjustment" && moveItem && (
              <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-xs" data-testid="current-stock-hint">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-amber-900">Aktueller Bestand</span>
                  <span className="font-mono text-lg font-bold text-amber-900" data-testid="current-stock-value">
                    {Number(moveItem.stock ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 3 })} {moveItem.unit || ""}
                  </span>
                </div>
                <div className="text-[10px] text-amber-800 mt-0.5">
                  Trage die tatsächlich gezählte Menge ein — das Delta wird als Korrektur gebucht (negativ = weniger, positiv = mehr).
                </div>
              </div>
            )}

            {moveItem?.container_size && (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 space-y-1.5" data-testid="scan-panel">
                <Label className="text-xs font-bold text-emerald-900 flex items-center gap-1">
                  <ScanLine size={14} /> Barcode scannen (+1 Gebinde)
                </Label>
                <Input
                  autoFocus
                  data-testid="input-movement-scan"
                  placeholder="Scanner hier fokussieren und scannen…"
                  className="font-mono"
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    const raw = (e.currentTarget.value || "").trim();
                    if (!raw) return;
                    const match = String(moveItem.barcode || "").trim() === raw
                                || String(moveItem.sku || "").trim().toLowerCase() === raw.toLowerCase();
                    if (!match) {
                      toast.error(`Barcode „${raw}" passt nicht zu ${moveItem.sku}`);
                      e.currentTarget.value = "";
                      return;
                    }
                    const next = (Number(form.qty_containers) || 0) + 1;
                    setForm((f) => ({ ...f, qty_containers: String(next), qty_direct: "" }));
                    toast.success(`+1 Gebinde · gesamt ${next}`);
                    e.currentTarget.value = "";
                    // Runde 124.8 – Scanner-Zoom: focus qty_direct so the
                    // operator can type the actual weight/count without
                    // clicking around after each scan.
                    setTimeout(() => {
                      const el = document.querySelector('[data-testid="input-movement-qty"]');
                      if (el) { el.focus(); el.select(); }
                    }, 50);
                  }}
                />
                <div className="text-[10px] text-emerald-800">
                  Jeder Scan erhöht „Anzahl Gebinde" um 1. Perfekt für Wareneingang mit Handscanner.
                </div>
              </div>
            )}

            {moveItem?.container_size && (
            <div>
              <Label>Buchungsart</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="select-movement-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in">Zugang (+)</SelectItem>
                  <SelectItem value="out">Abgang (−)</SelectItem>
                  <SelectItem value="adjustment">Korrektur (± je nach Vorzeichen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            {!moveItem?.container_size && (
            <div>
              <Label>Buchungsart</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="select-movement-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in">Zugang (+)</SelectItem>
                  <SelectItem value="out">Abgang (−)</SelectItem>
                  <SelectItem value="adjustment">Korrektur (± je nach Vorzeichen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {moveItem?.container_size && (
              <div className="rounded-lg border border-[var(--pop-pink)]/30 bg-[var(--pop-pink)]/5 p-3 space-y-1.5">
                <Label className="text-xs font-bold text-[var(--pop-pink)]">
                  Anzahl Gebinde · {moveItem.container_label || "Gebinde"} à {moveItem.container_size} {moveItem.container_unit || "kg"}
                </Label>
                <Input type="number" step="any" min="0" data-testid="input-movement-containers"
                       placeholder="z.B. 1"
                       value={form.qty_containers}
                       onChange={(e) => setForm({ ...form, qty_containers: normalizeDecimal(e.target.value), qty_direct: "" })} />
                {Number(form.qty_containers) > 0 && (
                  <div className="text-[10px] text-[var(--text-muted)] font-mono">
                    = {convertQty(Number(form.qty_containers) * Number(moveItem.container_size), moveItem.container_unit || moveItem.unit || "kg", moveItem.unit).toLocaleString("de-DE", { maximumFractionDigits: 3 })} {moveItem.unit || ""}
                  </div>
                )}
              </div>
            )}

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-1.5">
              <Label className="text-xs">
                {moveItem?.container_size ? "Oder direkt in kg/g/l/ml/stk eingeben" : "Menge"}
                {form.type === "adjustment" && " (negativ für Abgang)"}
              </Label>
              <div className="grid grid-cols-[1fr_120px] gap-2">
                <Input type="number" step="any" data-testid="input-movement-qty"
                       placeholder={DECIMAL_HINT}
                       value={form.qty_direct}
                       onChange={(e) => setForm({ ...form, qty_direct: normalizeDecimal(e.target.value), qty_containers: "" })} />
                <Select value={form.unit_direct || (moveItem?.unit || "stk")}
                        onValueChange={(v) => setForm({ ...form, unit_direct: v })}>
                  <SelectTrigger data-testid="select-movement-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="l">l</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="stk">Stück</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Bundle-Multiplikator: Fertigware/Bundles + Stück-Einheit.
                  Runde 124.9: Bundle-Presets kommen jetzt PRO Artikel aus
                  `item.bundles` (falls gepflegt), Fallback auf 13er/40er. */}
              {["finished", "bundle"].includes((moveItem?.type || "").toLowerCase())
                && (form.unit_direct || moveItem?.unit) === "stk" && (
                <div className="pt-1 flex items-center gap-2" data-testid="bundle-picker">
                  <Label className="text-xs whitespace-nowrap">Bundle:</Label>
                  <Select value={bundleSize} onValueChange={setBundleSize}>
                    <SelectTrigger data-testid="select-bundle-size" className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Einzelstück</SelectItem>
                      {(() => {
                        // Merge per-item bundles (from `item.bundles`) with the
                        // POPCRN defaults so specialty products (24er, 6er, …)
                        // appear automatically.
                        const perItem = moveItem?.bundles || {};
                        const found = new Map();
                        for (const [code, cfg] of Object.entries(perItem)) {
                          if (!cfg || cfg.enabled === false) continue;
                          const units = Number(cfg.units) || parseInt(String(code).replace(/[^0-9]/g, ""), 10);
                          if (!units || units <= 1) continue;
                          const label = cfg.label || `${units}er-Bundle`;
                          found.set(units, label);
                        }
                        // Sensible defaults if the item has no bundles configured.
                        if (found.size === 0) {
                          found.set(13, "13er-Bundle");
                          found.set(40, "40er-Bundle");
                        }
                        return [...found.entries()]
                          .sort((a, b) => a[0] - b[0])
                          .map(([units, label]) => (
                            <SelectItem key={units} value={String(units)}>{label}</SelectItem>
                          ));
                      })()}
                    </SelectContent>
                  </Select>
                  {Number(bundleSize) > 1 && (
                    <span className="text-[10px] font-mono text-[var(--text-muted)]">
                      × {bundleSize} = {(Number(form.qty_direct || 0) * Number(bundleSize)).toLocaleString("de-DE")} Stk
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Zielregal für Position 0 (optional). Runde 124.9 */}
            <div>
              <Label className="text-xs">Zielregal (optional)</Label>
              <Input value={shelfCode} onChange={(e) => setShelfCode(e.target.value)}
                     placeholder="z.B. R01-02-01"
                     className="font-mono"
                     data-testid="input-movement-shelf" />
              <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                Der Regalcode wird in die Buchungsnotiz übernommen.
              </div>
            </div>

            {/* Multi-Position-Liste (Runde 124.9): mehrere Buchungen in einem Save.
                Ideal für „10× 40er in R01-02-01 + 5× 13er in R02-01-01". */}
            <div className="rounded-xl border-2 border-dashed border-[var(--pop-pink)]/30 p-3"
                 data-testid="extra-positions">
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-[10px]">
                  Weitere Positionen ({extraPositions.length})
                </div>
                <button type="button"
                        onClick={() => setExtraPositions([...extraPositions, {
                          quantity: "", bundle_size: "1", shelf_code: "", batch_id: "", notes: "",
                        }])}
                        data-testid="btn-add-extra-position"
                        className="pop-pill bg-[var(--pop-pink)]/10 text-[var(--pop-pink)] text-[10px] font-bold px-2 py-1 hover:bg-[var(--pop-pink)]/20">
                  + Position
                </button>
              </div>
              {extraPositions.length === 0 && (
                <div className="text-[11px] text-[var(--text-muted)]">
                  Für Multi-Regal-Buchungen: „+ Position" fügt eine weitere Zeile mit
                  eigenem Bundle + Regal hinzu (gleicher Artikel, gleiche Buchungsart).
                </div>
              )}
              <div className="space-y-1.5">
                {extraPositions.map((p, i) => (
                  <div key={i}
                       className="grid grid-cols-[70px_110px_1fr_28px] gap-1.5 items-center"
                       data-testid={`extra-position-${i}`}>
                    <Input type="number" step="any" min="0" placeholder="Menge"
                           className="h-8 text-xs font-mono text-right"
                           data-testid={`extra-position-qty-${i}`}
                           value={p.quantity}
                           onChange={(e) => {
                             const c = [...extraPositions];
                             c[i].quantity = normalizeDecimal(e.target.value);
                             setExtraPositions(c);
                           }} />
                    <Select value={p.bundle_size}
                            onValueChange={(v) => {
                              const c = [...extraPositions]; c[i].bundle_size = v; setExtraPositions(c);
                            }}>
                      <SelectTrigger data-testid={`extra-position-bundle-${i}`}
                                     className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Einzel</SelectItem>
                        <SelectItem value="13">13er</SelectItem>
                        <SelectItem value="40">40er</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input value={p.shelf_code} placeholder="Regal z.B. R01-02-01"
                           className="h-8 text-xs font-mono"
                           data-testid={`extra-position-shelf-${i}`}
                           onChange={(e) => {
                             const c = [...extraPositions]; c[i].shelf_code = e.target.value;
                             setExtraPositions(c);
                           }} />
                    <button type="button"
                            onClick={() => setExtraPositions(extraPositions.filter((_, idx) => idx !== i))}
                            data-testid={`extra-position-remove-${i}`}
                            className="text-red-500 hover:text-red-700 justify-self-center">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {batches.length > 0 && (
              <div>
                <Label>Charge (optional)</Label>
                <Select value={batchId || "none"} onValueChange={(v) => setBatchId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="select-movement-batch"><SelectValue placeholder="Ohne Charge" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ohne Charge</SelectItem>
                    {batches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.charge_number} · MHD {b.mhd}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notiz</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                     placeholder="Inventur, Bruch, Umlagerung…" />
            </div>

            {/* Live preview of the booked base-unit quantity so warehouse
                staff can sanity-check BEFORE saving. Prevents the historic
                “13 kg looked right on the input but 130 000 g got booked”
                confusion by mirroring the exact math the submit will use. */}
            {(() => {
              const qty = computedQty();
              const base = moveItem?.unit || "";
              const type = form.type;
              const sign = type === "out" ? "−"
                        : type === "adjustment" ? (qty < 0 ? "−" : "+")
                        : "+";
              const abs = Math.abs(qty);
              const label = type === "in" ? "Zugang"
                          : type === "out" ? "Abgang"
                          : "Korrektur";
              const hasContainers = String(form.qty_containers || "").trim() !== ""
                                     && Number(form.qty_containers) > 0;
              const hasDirect = String(form.qty_direct || "").trim() !== ""
                                 && Number(form.qty_direct) !== 0;
              const source = hasDirect ? "Direkteingabe"
                           : hasContainers ? "Gebinde"
                           : "—";
              const current = Number(moveItem?.stock ?? 0);
              const nextStock = type === "in"      ? current + abs
                              : type === "out"     ? current - abs
                              : type === "adjustment" ? current + qty
                              : current;
              return (
                <div className="rounded-xl border-2 border-[var(--pop-pink)]/30 bg-[var(--pop-pink)]/5 p-3"
                     data-testid="movement-preview">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="overline text-[10px]">Vorschau · Buchungsmenge</div>
                      <div className="font-display font-black text-2xl text-[var(--pop-pink)] mt-0.5"
                           data-testid="movement-preview-qty">
                        {qty !== 0
                          ? <>{sign} {abs.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {base}</>
                          : <span className="text-[var(--text-muted)] text-lg">—</span>}
                      </div>
                      <div className="text-[10px] font-mono text-[var(--text-muted)] mt-0.5">
                        Quelle: {source} · {label}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="overline text-[10px]">Bestand nachher</div>
                      <div className="font-display font-black text-xl text-[var(--pop-black)] mt-0.5"
                           data-testid="movement-preview-next">
                        {nextStock.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {base}
                      </div>
                      <div className="text-[10px] font-mono text-[var(--text-muted)] mt-0.5">
                        vorher: {current.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {base}
                      </div>
                    </div>
                  </div>
                  {hasContainers && hasDirect && (
                    <div className="mt-2 text-[10px] rounded-md bg-amber-100 border border-amber-300 text-amber-900 px-2 py-1">
                      Zwei Eingaben aktiv — Direkteingabe hat Vorrang. Gebinde-Feld wird ignoriert.
                    </div>
                  )}
                  {nextStock < 0 && (
                    <div className="mt-2 text-[10px] rounded-md bg-red-100 border border-red-300 text-red-900 px-2 py-1">
                      Achtung: Bestand würde negativ.
                    </div>
                  )}
                </div>
              );
            })()}
            <DialogFooter>
              <Button type="submit" data-testid="btn-save-movement"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                Buchen ({Number(computedQty() || 0).toLocaleString("de-DE", { maximumFractionDigits: 3 })} {moveItem?.unit || ""})
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Bewegungen · {historyItem?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead className="overline">Datum</TableHead>
                  <TableHead className="overline">Typ</TableHead>
                  <TableHead className="overline text-right">Menge</TableHead>
                  <TableHead className="overline">Referenz</TableHead>
                  <TableHead className="overline">Notiz</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyRows.map((m) => {
                  const sign = m.type === "out" || m.type === "production_out" ? "−" : "+";
                  const color = sign === "+" ? "text-[var(--success)]" : "text-red-700";
                  return (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono text-xs">{m.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-xs">{moveTypeLabel(m.type)}</TableCell>
                      <TableCell className={`text-right font-mono ${color}`}>
                        {sign}{Math.abs(m.quantity)}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{m.reference || "—"}</TableCell>
                      <TableCell className="text-xs">{m.notes || "—"}</TableCell>
                    </TableRow>
                  );
                })}
                {historyRows.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-[var(--text-muted)]">Keine Bewegungen.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Batch edit dialog — für erstmalige Bestandsaufnahme */}
      <Dialog open={batchEditOpen} onOpenChange={setBatchEditOpen}>
        <DialogContent className="max-w-3xl" data-testid="dialog-batch-edit">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Chargen bearbeiten · {batchEditItem?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-[var(--text-muted)] -mt-1 mb-3">
            Chargen-Nummer und MHD sind direkt editierbar. Änderungen werden pro Zeile gespeichert.
            <b> Für die erstmalige Warenbestandsaufnahme.</b>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead className="overline">Chargen-Nr.</TableHead>
                  <TableHead className="overline">MHD</TableHead>
                  <TableHead className="overline">Regal</TableHead>
                  <TableHead className="overline text-right">Menge</TableHead>
                  <TableHead className="overline text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchEditRows.map((b) => (
                  <TableRow key={b.id} data-testid={`batch-edit-row-${b.charge_number}`}>
                    <TableCell>
                      <input type="checkbox"
                             checked={!!batchSel[b.id]}
                             data-testid={`batch-select-${b.id}`}
                             onChange={(e) => setBatchSel({ ...batchSel, [b.id]: e.target.checked })} />
                    </TableCell>
                    <TableCell>
                      <Input value={b.charge_number || ""}
                             data-testid={`input-batch-charge-${b.id}`}
                             onChange={(e) => updateBatchField(b.id, "charge_number", e.target.value)}
                             className="h-8 font-mono text-sm" />
                    </TableCell>
                    <TableCell>
                      <Input type="date" value={(b.mhd || "").slice(0, 10)}
                             data-testid={`input-batch-mhd-${b.id}`}
                             onChange={(e) => updateBatchField(b.id, "mhd", e.target.value)}
                             className="h-8 font-mono text-sm" />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{b.location || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{b.quantity} {batchEditItem?.unit}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <LabelPickerButton
                        testid={`batch-label-${b.id}`}
                        label="Etikett"
                        category="charge"
                        data={{
                          sku: batchEditItem?.sku,
                          item_name: batchEditItem?.name,
                          charge_number: b.charge_number,
                          mhd: (b.mhd || "").slice(0, 10),
                          location: b.location,
                          quantity: `${b.quantity} ${batchEditItem?.unit || ""}`,
                          barcode: b.charge_number,
                        }}
                      />
                      <button onClick={() => saveBatch(b)} disabled={!b._dirty}
                              data-testid={`btn-save-batch-${b.id}`}
                              className={`ml-3 text-xs font-bold inline-flex items-center gap-1 ${
                                b._dirty ? "text-[var(--pop-pink)] hover:underline" : "text-[var(--text-muted)] cursor-not-allowed"
                              }`}>
                        <Save size={12} /> {b._dirty ? "Speichern" : "Gespeichert"}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {batchEditRows.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-[var(--text-muted)]">
                    Noch keine Chargen für diesen Artikel.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            {/* Bulk-Etiketten-Druck-Leiste (Runde 117) */}
            {Object.values(batchSel).some(Boolean) && (
              <div className="mt-3 flex items-center justify-between rounded-xl bg-[var(--pop-yellow)]/20 border border-[var(--pop-yellow)] px-3 py-2">
                <div className="text-sm font-semibold" data-testid="batch-selection-count">
                  {Object.values(batchSel).filter(Boolean).length} Charge(n) ausgewählt
                </div>
                <LabelPickerButton
                  testid="batch-bulk-print"
                  size="md"
                  label={`${Object.values(batchSel).filter(Boolean).length} Etiketten drucken`}
                  category="charge"
                  bulkRecords={batchEditRows
                    .filter((b) => batchSel[b.id])
                    .map((b) => ({
                      sku: batchEditItem?.sku,
                      item_name: batchEditItem?.name,
                      charge_number: b.charge_number,
                      mhd: (b.mhd || "").slice(0, 10),
                      location: b.location,
                      quantity: `${b.quantity} ${batchEditItem?.unit || ""}`,
                      barcode: b.charge_number,
                    }))}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail-Drawer (Sidepanel) */}
      <Sheet open={!!detailItem} onOpenChange={(o) => { if (!o) setDetailItem(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] text-[var(--text)] border-[var(--border)]" data-testid="stock-detail-drawer">
          {detailItem && (() => {
            const r = detailItem; const st = statusOf(r);
            const locs = [...new Set((detailBatches || []).map((b) => b.location).filter(Boolean))];
            return (
              <div className="space-y-5">
                <SheetHeader className="text-left">
                  <div className="font-mono text-[11px] text-[var(--text-muted)]">{r.sku}</div>
                  <SheetTitle className="font-display text-2xl text-[var(--text)]">{r.name}</SheetTitle>
                  <div className="flex items-center gap-2">
                    <TypeBadge type={r.type} />
                    {st !== "ok" && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st === "critical" ? "bg-red-500/15 text-red-600" : "bg-amber-500/15 text-amber-700"}`}>{st === "critical" ? "KRITISCH" : "WARNUNG"}</span>}
                  </div>
                </SheetHeader>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { l: "Gesamtbestand", v: `${num(r.stock)} ${r.unit}` },
                    { l: "Reserviert", v: Number(r.reserved || 0) > 0 ? `${num(r.reserved)} ${r.unit}` : "—" },
                    { l: "Verfügbar", v: `${num(r.available ?? r.stock)} ${r.unit}`, hi: true },
                    { l: "Mindestbestand", v: `${num(r.min_stock, 0)} ${r.unit}` },
                  ].map((x) => (
                    <div key={x.l} className="rounded-xl border border-[var(--border)] p-3">
                      <div className="overline text-[10px] text-[var(--text-muted)]">{x.l}</div>
                      <div className={`font-mono font-bold text-lg ${x.hi ? (st === "critical" ? "text-red-600" : st === "warning" ? "text-amber-600" : "text-[var(--text)]") : "text-[var(--text)]"}`}>{x.v}</div>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="overline text-[10px] text-[var(--text-muted)] mb-2 flex items-center gap-1"><MapPin size={12} /> Lagerplätze</div>
                  {hasLoc(r) || locs.length ? (
                    <div className="flex flex-wrap gap-2" data-testid="drawer-locations">
                      {hasLoc(r) && <span className="font-mono text-sm px-2 py-1 rounded-lg border border-[var(--border)]">{r.warehouse_location}</span>}
                      {locs.filter((l) => l !== r.warehouse_location).map((l) => (
                        <span key={l} className="font-mono text-sm px-2 py-1 rounded-lg border border-[var(--border)]">{l}</span>
                      ))}
                    </div>
                  ) : <div className="text-sm text-[var(--text-muted)]">Kein Lagerplatz zugewiesen</div>}
                </div>

                <div>
                  <div className="overline text-[10px] text-[var(--text-muted)] mb-2 flex items-center gap-1"><Boxes size={12} /> Chargen / MHD</div>
                  {detailBatches.length ? (
                    <div className="space-y-1.5">
                      {detailBatches.map((b) => (
                        <div key={b.id} data-testid={`detail-batch-${b.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--border)] p-2 text-sm">
                          <div className="min-w-0">
                            <div className="font-mono truncate">{b.charge_number || "—"}</div>
                            <div className="text-[11px] text-[var(--text-muted)]">MHD {b.mhd ? fmtDate(b.mhd) : "—"}{b.location ? ` · ${b.location}` : ""}</div>
                          </div>
                          <div className="font-mono text-sm shrink-0">{num(b.quantity)} {r.unit}</div>
                        </div>
                      ))}
                    </div>
                  ) : <div className="text-sm text-[var(--text-muted)]">Keine Chargen / kein MHD hinterlegt</div>}
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Button data-testid="drawer-btn-in" onClick={() => openMovement(r, "in")} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"><Plus size={14} className="mr-1" /> Zugang</Button>
                  <Button data-testid="drawer-btn-out" onClick={() => openMovement(r, "out")} className="rounded-lg bg-red-600 hover:bg-red-700 text-white"><Minus size={14} className="mr-1" /> Abgang</Button>
                  <Button data-testid="drawer-btn-adj" variant="outline" onClick={() => openMovement(r, "adjustment")} className="rounded-lg"><ArrowUpDown size={14} className="mr-1" /> Korrektur</Button>
                  <Button data-testid="drawer-btn-batches" variant="outline" onClick={() => openBatchEdit(r)} className="rounded-lg"><Package size={14} className="mr-1" /> Chargen</Button>
                  <Button data-testid="drawer-btn-hist" variant="outline" onClick={() => openHistory(r)} className="rounded-lg col-span-2"><History size={14} className="mr-1" /> Verlauf</Button>
                </div>
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
